import {
  Flex,
  Checkbox,
  Text,
  Spinner,
  Input,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CopyConfig } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';

interface Props {
  compositionGroupId: number;
  linkedVariantId?: number | null;
  sourceGroupNote?: string | null;
  buttonLabel: string;
  onConfirm: (id: number, copyConfig: CopyConfig) => void;
}

export const CompositionGroupCopyConfig = ({
  compositionGroupId,
  linkedVariantId,
  sourceGroupNote,
  buttonLabel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const {
    data: availability,
    loading,
    refetch,
  } = useApiResource(variantsApi.getVariantDataAvailability);

  const {
    setValue,
    register,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CopyConfig>({
    defaultValues: {
      optionName: sourceGroupNote ?? undefined,
      copySuppliersAndPurchaseInfo: true,
      copySalesPrices: true,
      copyGarpData: true,
    },
  });

  useEffect(() => {
    if (!linkedVariantId) return;
    refetch(linkedVariantId);
  }, [linkedVariantId, refetch]);

  useEffect(() => {
    if (!availability) return;

    setValue('copySuppliersAndPurchaseInfo', availability.hasSuppliers);
    setValue('copySalesPrices', availability.hasSalesPrices);
    setValue('copyGarpData', availability.hasGarpData);
  }, [availability, setValue]);

  const onSubmit = (data: CopyConfig) => onConfirm(compositionGroupId, data);

  return (
    <Flex
      direction="column"
      gap={4}
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
          {t('compositionGroup.copy.newOptionName')}
        </FormLabel>
        <Input
          size="sm"
          {...register('optionName')}
          placeholder={t('compositionGroup.copy.enterOptionName')}
        />
      </FormControl>
      {linkedVariantId && (
        <Flex direction="column" gap={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t('compositionGroup.copy.copyDataFromSource')}
          </Text>
          {loading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <Flex direction="column" gap={1}>
                <Checkbox
                  {...register('copySuppliersAndPurchaseInfo')}
                  isChecked={watch('copySuppliersAndPurchaseInfo')}
                  isDisabled={!availability?.hasSuppliers}
                >
                  {t('compositionGroup.copy.copySuppliersAndPurchaseInfo')}
                </Checkbox>
                {!availability?.hasSuppliers && (
                  <Text fontSize="xs" color="gray.400" ml={6}>
                    {t('compositionGroup.copy.noSuppliersAvailable')}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap={1}>
                <Checkbox
                  {...register('copySalesPrices')}
                  isChecked={watch('copySalesPrices')}
                  isDisabled={!availability?.hasSalesPrices}
                >
                  {t('compositionGroup.copy.copySalesPrices')}
                </Checkbox>
                {!availability?.hasSalesPrices && (
                  <Text fontSize="xs" color="gray.400" ml={6}>
                    {t('compositionGroup.copy.noSalesPricesAvailable')}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap={1}>
                <Checkbox
                  {...register('copyGarpData')}
                  isChecked={watch('copyGarpData')}
                  isDisabled={!availability?.hasGarpData}
                >
                  {t('compositionGroup.copy.copyGarpData')}
                </Checkbox>
                {!availability?.hasGarpData && (
                  <Text fontSize="xs" color="gray.400" ml={6}>
                    {t('compositionGroup.copy.noGarpDataAvailable')}
                  </Text>
                )}
              </Flex>
            </>
          )}
        </Flex>
      )}
      <SubmitButton loading={isSubmitting}>{buttonLabel}</SubmitButton>
    </Flex>
  );
};
